/**
 * Functionality to export the Piano data entries to the following output formats:
 * - PDF
 *
 * @example
 *
 * import { EntryExporter, FORMATS } from './utils/export/entry';
 *
 * EntryExporter.exportSingle(
 *     'My inventory', fields, FORMATS.PDF
 * ).then(stream => {
 *     console.log('Export was successful!');
 * }).fail(err => {
 *     console.error(err);
 * });
 */

import _ from "lodash";
import BlobStream from "blob-stream-browserify";
import PDFDocument from "pdfkit-browserify";

import DateConfig from "../../dateConfig";
import { Field } from "../../components/common/form/FieldHandler";
import { ImageObject } from "../../hooks/database/types/inventories";
import { PouchDatabaseManager } from "../../hooks/database/manager";

/**==============================================================================
 * Constants
 */

/**
 * Enum with the different image sizes available.
 */
enum ImageSizes {
  THUMBNAIL = "THUMBNAIL",
  ORIGINAL = "ORIGINAL",
}

/**
 * Enum with the export formats that are currently implemented.
 */
enum Formats {
  PDF = "PDF",
}

/**==============================================================================
 * Internal functionality
 */

const PDF_FONTSIZE = {
  H1: 24,
  H2: 18,
  H3: 14,
  TEXT: 12,
};

class PDFBuilderBase {
  static insertH1(doc: typeof PDFDocument, text: string) {
    doc.fontSize(PDF_FONTSIZE.H1);
    doc.text(text);
    doc.fontSize(PDF_FONTSIZE.TEXT);
    doc.moveDown();
  }
  static insertH2(doc: typeof PDFDocument, text: string) {
    doc.fontSize(PDF_FONTSIZE.H2);
    doc.text(text);
    doc.fontSize(PDF_FONTSIZE.TEXT);
    doc.moveDown();
  }
  static insertH3(doc: typeof PDFDocument, text: string) {
    doc.fontSize(PDF_FONTSIZE.H3);
    doc.text(text);
    doc.fontSize(PDF_FONTSIZE.TEXT);
    doc.moveDown();
  }
  static insertParagraph(doc: typeof PDFDocument, p: string[] | string) {
    doc.fontSize(PDF_FONTSIZE.TEXT);
    if (!Array.isArray(p)) {
      p = [p];
    }
    p.forEach((pi) => doc.text(pi));
    doc.moveDown();
    return doc;
  }
  static buildPdfHeader(
    doc: typeof PDFDocument,
    metadata: {
      name: string;
      numProjects: number;
      numInventories: number;
      numForms: number;
      dateStart: string;
      dateEnd: string;
      projects: string[];
      inventories: string[];
    }
  ) {
    PDFBuilderBase.insertH1(doc, 'Piano rapport - "' + metadata.name + '"');
    PDFBuilderBase.insertParagraph(doc, "Rapport skapad " + new Date().toLocaleString("sv-SE"));
    PDFBuilderBase.insertH2(doc, "Innehåll");
    PDFBuilderBase.insertParagraph(doc, ["Metadata", "Projekt och inventeringar i denna rapport", "Formulär"]);
    doc.moveDown();

    PDFBuilderBase.insertH2(doc, "Metadata");
    const text1 = `Denna rapport är baserad på ${metadata.numProjects} projekt, ${metadata.numInventories} inventering${
      metadata.numInventories === 1 ? "" : "ar"
    } och ${metadata.numForms} formulär.`;
    const text2 = "Rapporten beaktar enbart formulär med fältet report = " + metadata.name + ".";
    let text3;
    if (
      metadata.dateStart === DateConfig.MinDate.toLocaleDateString("sv-SE") &&
      metadata.dateEnd === DateConfig.MaxDate.toLocaleDateString("sv-SE")
    ) {
      text3 = "Formulären är inte filtrerade på datum.";
    } else {
      text3 = "Formulären är filtrerade på datum, fr.o.m. " + metadata.dateStart + " t.o.m. " + metadata.dateEnd + ".";
    }
    PDFBuilderBase.insertParagraph(doc, [text1, text2, text3]);

    PDFBuilderBase.insertH2(doc, "Work areas och inventeringar i denna rapport");
    PDFBuilderBase.insertParagraph(doc, [
      "Work areas: " + metadata.projects.join(", "),
      "Inventeringar: " + metadata.inventories.join(", "),
    ]);

    doc.addPage();
    PDFBuilderBase.insertH2(doc, "Formulär");
  }
}

/**
 * Takes care of building PDF documents using the pdfkit library.
 */
class PDFBuilder extends PDFBuilderBase {
  /**
   * Inserts a field containing images into the given PDF-document.
   *
   * @param {PDFDocument} doc - Document to insert into
   * @param {object} field - The field to get the value from
   * @returns {PDFDocument}
   */
  static insertImageField(doc: typeof PDFDocument, field: Field & { type: "image" }) {
    doc.fontSize(PDF_FONTSIZE.H2);
    doc.text(field.label!);
    field.value!.forEach((v) => {
      doc.image(v);
    });
    doc.moveDown();
  }

  /**
   * Inserts a field containing text into the given PDF-document.
   *
   * @param {PDFDocument} doc - Document to insert into
   * @param {object} field - The field to get the value from
   * @returns {PDFDocument}
   */
  static insertTextField(doc: typeof PDFDocument, field: Field & { type: "text" }) {
    doc.fontSize(PDF_FONTSIZE.H2);
    doc.text(field.label!);
    doc.fontSize(PDF_FONTSIZE.TEXT);
    doc.text(field.value!);
    doc.moveDown();
  }

  /**
   * Inserts a inventory along with its fields into given PDF-document.
   *
   * @param doc
   * @param {string} title - Title of the inventory
   * @param {Array.<Field>} fields - Fields to insert
   * @returns {PDFDocument} - Same document as input, for chainability
   */
  static insertInventory(doc: typeof PDFDocument, title: string, fields: Field[]) {
    doc.fontSize(25);
    doc.text(title);
    doc.moveDown();

    fields.forEach((field) => {
      switch (field.type) {
        case "image":
          PDFBuilder.insertImageField(doc, field);
          break;
        default:
          PDFBuilder.insertTextField(doc, field as any);
          break;
      }
    });
    return doc;
  }

  /**
   * Generates a PDF-document from the given fields of an data entry and
   * returns a HTML5 blob with the binary data.
   *
   * @param {string} title - Title of the inventory
   * @param {Array.<Field>} fields - Fields to export
   * @returns {Promise}
   */
  static buildSingle(title: string, fields: Field[]) {
    return new Promise((resolve, reject) => {
      if (!_.isString(title)) {
        reject(`Given title was not a string: ${title}`);
        return;
      }

      if (!_.isArray(fields)) {
        reject(`Given fields was not an array: ${fields}`);
        return;
      }

      const doc = new PDFDocument();
      const stream = doc.pipe(BlobStream());

      PDFBuilder.insertInventory(doc, title, fields).end();

      stream.on("finish", () => resolve(stream));
      stream.on("error", (err: any) => reject(err));
    });
  }
}

/**
 * Handles loading images from the database, converting between different data
 * formats, etc.
 */
class ImageHandler {
  /**
   * Retrieves the content of given image as a base64 encoded string.
   */
  static async getAsBase64(image: ImageObject, size: ImageSizes, inventoryId: string, mngr: PouchDatabaseManager) {
    if (!("imageId" in image)) {
      throw new Error("Image variant not supported");
    }
    const blob =
      size === ImageSizes.THUMBNAIL
        ? await mngr.getThumbnail(inventoryId, image.thumbnailId)
        : await mngr.getImage(inventoryId, image.imageId);
    return new Promise<string>((resolve) => {
      const reader = new FileReader();

      reader.onload = (event) => resolve(event.target!.result as string);
      reader.readAsDataURL(blob as Blob);
    });
  }
}

/**
 * Handles preprocessing data before outputs are generated. Some values are only
 * saved as references to the actual data, this utility class takes care of
 * loading all data references.
 */
class Preprocessor {
  /**
   * Makes sure the images are loaded and doesn't contain a URL that pdfkit
   * can't resolve.
   */
  static async processImageField(field: Field & { type: "image" }, inventoryId: string, mngr: PouchDatabaseManager) {
    if (!_.isArray(field.value)) {
      throw new Error(`Expected given image field to have an array of image objects, but got: ${field.value}`);
    }

    const sources = await Promise.all(field.value.map((v) => ImageHandler.getAsBase64(v, ImageSizes.THUMBNAIL, inventoryId, mngr)));
    return { ...field, value: sources };
  }

  /**
   * Checks that the given field is a valid field, by seeing if the field is
   * of right type, has the required keys, etc.
   *
   * @param {object} field
   * @returns {boolean}
   */
  static isFieldValid(field: { type?: any; value?: any }) {
    return _.isObject(field) && ["type", "value"].every((k) => k in field);
  }

  /**
   * Makes sure all data of the given data entry are loaded and ready for
   * export.
   */
  static processFields(fields: Field[], inventoryId: string, mngr: PouchDatabaseManager) {
    if (!Array.isArray(fields)) {
      throw new Error(`Fields was not an array: ${fields}`);
    }

    return Promise.all(
      fields.map(async (field, index) => {
        if (!Preprocessor.isFieldValid(field)) {
          console.warn(
            `Field (index: ${index}) was not a valid field (see Preprocessor.isValidField), and was therefor filtered out from the export.`
          );

          return field;
        }

        if (field.type === "image") {
          return Preprocessor.processImageField(field, inventoryId, mngr);
        }

        return field;
      })
    );
  }
}

/**============================================================================*/

/**
 * External interface for the module, uses internal classes for preprocessing
 * and output generation.
 */
class EntryExporter {
  /**
   * Exports given data entry fields to given format, returns a promise that
   * when resolved contains a blob-stream.
   *
   * @param title - Title of geom to export
   * @param fields - Fields of entry to export
   * @param format - Export format to use
   * @param inventoryId
   * @param mngr
   * @returns {Promise}
   */
  static async exportSingle(
    title: string,
    fields: Field[] | Record<string, Field>,
    format: Formats,
    inventoryId: string,
    mngr: PouchDatabaseManager
  ) {
    let fieldsClone = null;

    if (Array.isArray(fields)) {
      fieldsClone = fields;
    } else if (_.isObject(fields)) {
      fieldsClone = Object.values(fields);
    } else {
      throw new Error(`Expected fields as array/object, got: ${fields}`);
    }

    const processed = await Preprocessor.processFields(_.cloneDeep(fieldsClone), inventoryId, mngr);
    return PDFBuilder.buildSingle(title, processed as any);
  }
}

export { EntryExporter, Formats };
